import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { paginarPorAltura, PaginaDaColuna } from './paginarPorAltura';

/**
 * Mede os itens de uma coluna do Telão e a corta em páginas (at-130).
 *
 * Como funciona: com `medindo = true` a coluna renderiza TODOS os itens; o `useLayoutEffect`
 * mede cada um (antes da pintura, então não pisca), calcula as páginas e desliga a medição.
 * Remede quando os itens mudam, quando o corpo muda de tamanho (`ResizeObserver`) e quando a
 * fonte termina de carregar (a AmpleSoft muda a altura da placa).
 *
 * Os itens se identificam no DOM com `data-item="<índice>"` e, os cabeçalhos, com
 * `data-cabecalho`.
 *
 * 🔴 No CSS, os filhos do corpo precisam de `flex: none`. Sem isso o flex ESPREME os itens para
 * caber, a medição conclui que tudo cabe numa página e as placas saem cortadas — defeito
 * reproduzido no protótipo em 23/10/2026.
 */
export function usePaginacaoPorAltura(assinaturaDosItens: string) {
  const corpoRef = useRef<HTMLDivElement>(null);
  const [medindo, setMedindo] = useState(true);
  const [paginas, setPaginas] = useState<PaginaDaColuna[]>([{ itens: [], continuaDe: null }]);

  useLayoutEffect(() => {
    if (!medindo) return;
    const corpo = corpoRef.current;
    if (!corpo) return;

    const filhos = (Array.from(corpo.children) as HTMLElement[]).filter((el) => el.hasAttribute('data-item'));
    const alturas: number[] = [];
    const ehCabecalho: boolean[] = [];
    let alturaCabecalho = 0;
    filhos.forEach((el) => {
      const i = Number(el.dataset.item);
      const estilo = window.getComputedStyle(el);
      const h = el.offsetHeight + parseFloat(estilo.marginTop || '0') + parseFloat(estilo.marginBottom || '0');
      alturas[i] = h;
      ehCabecalho[i] = el.hasAttribute('data-cabecalho');
      if (ehCabecalho[i] && !alturaCabecalho) alturaCabecalho = h;
    });

    const estiloCorpo = window.getComputedStyle(corpo);
    const capacidade =
      corpo.clientHeight - parseFloat(estiloCorpo.paddingTop || '0') - parseFloat(estiloCorpo.paddingBottom || '0');

    setPaginas(paginarPorAltura({ alturas, ehCabecalho, alturaCabecalhoCont: alturaCabecalho, capacidade }));
    setMedindo(false);
  }, [medindo]);

  // Itens mudaram (carga, refetch, socket): remede.
  useEffect(() => {
    setMedindo(true);
  }, [assinaturaDosItens]);

  // Corpo mudou de tamanho (janela, tela cheia, TV): remede, com folga de 150 ms.
  useEffect(() => {
    const corpo = corpoRef.current;
    if (!corpo || typeof ResizeObserver === 'undefined') return undefined;
    let espera: ReturnType<typeof setTimeout> | undefined;
    let ultimaAltura = corpo.clientHeight;
    const observador = new ResizeObserver(() => {
      if (corpo.clientHeight === ultimaAltura) return;
      ultimaAltura = corpo.clientHeight;
      clearTimeout(espera);
      espera = setTimeout(() => setMedindo(true), 150);
    });
    observador.observe(corpo);
    return () => {
      clearTimeout(espera);
      observador.disconnect();
    };
  }, []);

  // A fonte de display muda a altura das placas quando termina de carregar.
  useEffect(() => {
    let vivo = true;
    const fontes = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    fontes?.ready?.then(() => {
      if (vivo) setMedindo(true);
    });
    return () => {
      vivo = false;
    };
  }, []);

  return { corpoRef, medindo, paginas };
}
